// hooks
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

// utils
import { toBackendDateTime, toDateString } from 'utils/date';
import { getRegisterAbsenceDutyHourTypes } from 'RestAPIs/roster/register-absence';

// types
import type { PublicDutyHourType } from 'RestAPIs/roster/register-absence';

// Absences registered from the notice flows are sickness reports, so every
// duty hour type on dcc 5 (ziekte, geen ORT) is offered — ZK, ZKZ, but also
// e.g. reintegratie and zwangerschapsverlof. Widened from only ZK/ZKZ on
// 8 aug 2026: the dcc is the definition of "sickness", not the two codes.
const SICKNESS_DCC_SVC = 5;

// ziekte (ZK) is the default choice, so it sorts first
const DEFAULT_DUTY_HOUR_TYPE_CODE = 'ZK';

// a stable empty list, so a consumer keyed on it does not recompute while the
// query has no answer yet
const NO_DUTY_HOUR_TYPES: PublicDutyHourType[] = [];

type UseDutyHourTypesArgs = {
  open: boolean;
  /** edit mode: the loaded absence dictates the type, nothing may override it */
  isEdit: boolean;
  hasValidPeriod: boolean;
  startDate: Date | null;
  endDate: Date | null;
  today: Date;
};

/** The sickness duty hour types for the period, ZK first as the default
 *  choice, and the chosen type — preselected as soon as the current choice is
 *  no longer on offer. */
export const useDutyHourTypes = ({
  open,
  isEdit,
  hasValidPeriod,
  startDate,
  endDate,
  today
}: UseDutyHourTypesArgs) => {
  'use memo';
  const [dutyTypeId, setDutyTypeId] = useState<number | ''>('');

  const dutyHourTypesQuery = useQuery(
    ['registerAbsenceSicknessDutyHourTypes', toDateString(startDate ?? today)],
    async () => {
      // the legacy endpoints expect full datetimes (yyyy-MM-dd'T'HH:mm:ss.SSS'Z')
      const { data } = await getRegisterAbsenceDutyHourTypes({
        startDate: toBackendDateTime(startDate ?? today),
        endDate: toBackendDateTime(endDate ?? startDate ?? today, true)
      });
      return (
        data
          .filter(
            (dutyHourType: PublicDutyHourType) =>
              dutyHourType.dccSvc === SICKNESS_DCC_SVC
          )

          // ziekte (ZK) always first as the default choice, the rest readable
          .sort((a, b) => {
            if (a.code === DEFAULT_DUTY_HOUR_TYPE_CODE) {
              return -1;
            }
            if (b.code === DEFAULT_DUTY_HOUR_TYPE_CODE) {
              return 1;
            }
            return a.description.localeCompare(b.description);
          })
      );
    },
    { enabled: open && hasValidPeriod, keepPreviousData: true }
  );

  // preselect when only one sickness type remains; in edit mode the loaded
  // absence dictates the type, so nothing may override it
  useEffect(() => {
    const types = dutyHourTypesQuery.data;
    if (!types?.length || isEdit) {
      return;
    }
    setDutyTypeId((current) =>
      types.some((type) => type.dutyTypeId === current)
        ? current
        : types[0].dutyTypeId
    );
  }, [dutyHourTypesQuery.data, isEdit]);

  return {
    dutyHourTypes: dutyHourTypesQuery.data ?? NO_DUTY_HOUR_TYPES,
    // an empty list only means something once it is an answer: while the query
    // runs it is empty too, and a warning would flash up on every open
    hasDutyHourTypesResult: !!dutyHourTypesQuery.data,
    dutyTypeId,
    setDutyTypeId
  };
};
